import { useTranslation } from 'react-i18next';
import { GgopPartyPicker } from '@/components/ggop/GgopPartyPicker';
import type { Party } from '@/lib/xdis/xdis18Form';
import {
  RECEIVER_LIMITS,
  XDIS_IDENTIFIER_CODES,
  partyForMode,
  receiverIssues,
  type ReceiverChoice,
  type ReceiverMode,
} from '@/lib/xdis/xdisReceiver';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';

const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';

// Receiver of an XDIS91 answer or XDIS18 (XDIS-EPJ 5.4 / 7.8): the default (the letter's
// sender), MedCom Testcenter, a unit from SOR (same picker as GGOP), or a typed EAN.

export function XdisReceiverChooser({
  fieldId,
  choice,
  onChange,
  defaultLabel,
  defaultParty,
  testcenterEan,
  showErrors,
}: {
  fieldId: string;
  choice: ReceiverChoice;
  onChange: (choice: ReceiverChoice) => void;
  defaultLabel: string;
  /** Shown read-only for the default; null when only Communicate knows it (XDIS91 answer). */
  defaultParty: Party | null;
  testcenterEan: string;
  showErrors: boolean;
}) {
  const { t } = useTranslation();
  const modes: { mode: ReceiverMode; label: string }[] = [
    { mode: 'default', label: defaultLabel },
    { mode: 'testcenter', label: t('patients.xdis.receiver_testcenter', 'MedCom Testcenter ({{ean}})', { ean: testcenterEan }) },
    { mode: 'sor', label: t('patients.xdis.receiver_sor', 'Anden modtager fra SOR') },
    { mode: 'manual', label: t('patients.xdis.receiver_manual', 'Indtast lokationsnummer (EAN)') },
  ];
  const party = choice.party;
  const set = (patch: Partial<Party>) => party && onChange({ ...choice, party: { ...party, ...patch } });
  const issues = choice.mode === 'default' ? [] : receiverIssues(party);

  return (
    <div className="space-y-2" data-testid={`${fieldId}-chooser`}>
      <fieldset className="space-y-1">
        <legend className={FORM_LABEL_TYPE}>{t('patients.xdis.receiver', 'Modtager')}</legend>
        {modes.map((m) => (
          <label key={m.mode} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`${fieldId}-mode`}
              value={m.mode}
              checked={choice.mode === m.mode}
              onChange={() => onChange({ mode: m.mode, party: partyForMode(m.mode, testcenterEan, party) })}
              data-testid={`${fieldId}-mode-${m.mode}`}
            />
            {m.label}
          </label>
        ))}
      </fieldset>

      {choice.mode === 'default' && defaultParty && (
        <p className={FORM_HINT_TYPE} data-testid={`${fieldId}-default`}>
          {[defaultParty.organisationName, `EAN ${defaultParty.ean}`, `${defaultParty.identifier} (${defaultParty.identifierCode})`].filter(Boolean).join(' · ')}
        </p>
      )}

      {choice.mode === 'sor' && (
        <GgopPartyPicker
          fieldId={`${fieldId}-sor`}
          label={t('patients.xdis.receiver_sor_search', 'Modtager fra SOR')}
          value={party ? { ...party, organisationName: party.organisationName ?? '' } : null}
          onChange={(v) => onChange({ mode: 'sor', party: v ? { ...v, organisationName: v.organisationName || undefined } : null })}
          identifierCodes={XDIS_IDENTIFIER_CODES}
        />
      )}

      {(choice.mode === 'testcenter' || choice.mode === 'manual') && party && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('patients.xdis.receiver_ean', 'Lokationsnummer (EAN)')}</span>
            <input
              className={FIELD}
              inputMode="numeric"
              maxLength={13}
              value={party.ean}
              onChange={(e) => set({ ean: e.target.value.replace(/\D/g, '') })}
              data-testid={`${fieldId}-ean`}
            />
          </label>
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('patients.xdis.receiver_org', 'Organisation')}</span>
            <input className={FIELD} maxLength={RECEIVER_LIMITS.organisationName} value={party.organisationName ?? ''} onChange={(e) => set({ organisationName: e.target.value })} />
          </label>
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('patients.xdis.receiver_identifier_code', 'Identifikatorkode')}</span>
            <select className={FIELD} value={party.identifierCode} onChange={(e) => set({ identifierCode: e.target.value })}>
              {XDIS_IDENTIFIER_CODES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={FORM_LABEL_TYPE}>{t('patients.xdis.receiver_identifier', 'Identifikator')}</span>
            <input
              className={FIELD}
              maxLength={RECEIVER_LIMITS.identifier}
              value={party.identifier}
              onChange={(e) => set({ identifier: e.target.value })}
              data-testid={`${fieldId}-identifier`}
            />
          </label>
        </div>
      )}

      {showErrors &&
        issues.map((message) => (
          <p key={message} role="alert" className={FORM_ERROR_TYPE}>
            {message}
          </p>
        ))}
    </div>
  );
}
